import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  get_order,
  upload_document,
  getDocuments,
} from "../../store/reducers/orderReducer";
import { MdDelete } from "react-icons/md";
import { IoIosCloudDone } from "react-icons/io";
import { FaCopy } from "react-icons/fa6";
import Loader from "../Loader";
import toast from "react-hot-toast";

const Order = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const { myOrder } = useSelector((state) => state.order);
  const { userInfo } = useSelector((state) => state.auth);
  const [documents, setDocuments] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch(get_order(orderId));
    dispatch(getDocuments(orderId))
      .then(({ payload }) => {
        setDocuments(payload);
      })
      .catch((err) => {
        console.error("Failed to fetch documents:", err);
      });
  }, [orderId, dispatch]);

  // Handle document upload
  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert(
        "La taille du fichier dépasse 2 Mo, Veuillez télécharger un fichier plus petit!"
      );
      return;
    }

    const fileType = file.type;
    if (!["image/png", "image/jpeg", "application/pdf"].includes(fileType)) {
      alert("Only PNG, JPG, or PDF files are allowed.");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setDocuments((prevDocs) => [
        ...prevDocs,
        { file, preview: reader.result },
      ]);
    };
    reader.readAsDataURL(file);
  };

  // Remove a document
  const handleRemove = (index) => {
    const updatedDocs = [...documents];
    updatedDocs.splice(index, 1);
    setDocuments(updatedDocs);
  };

  // Submit documents
  const handleSubmit = () => {
    // Check if there are new files selected
    const newFiles = documents.filter((doc) => doc.file);

    // If no new files are selected, show an alert
    if (newFiles.length === 0) {
      alert(
        "Aucun fichier sélectionné, veuillez sélectionner des documents depuis votre appareil !"
      );
      return; // Exit the function
    }

    const formData = new FormData();
    formData.append("orderId", orderId);

    // Add documents to FormData (only new files)
    newFiles.forEach((doc) => {
      formData.append("documents", doc.file); // Add new files
    });

    // Debugging: Log FormData content
    for (let [key, value] of formData.entries()) {
      console.log(`${key}:`, value);
    }

    // Show loader
    setIsLoading(true);

    // Dispatch the upload action
    dispatch(upload_document({ orderId, documents: formData }))
      .then(() => {
        setIsSubmitted(true); // Set the form as submitted

        // Keep loader visible for 3 seconds before reloading
        setTimeout(() => {
          setIsLoading(false); // Hide the loader
          window.location.reload(); // Reload the page
        }, 3000);
      })
      .catch((err) => {
        setIsLoading(false); // Hide the loader on error
        console.error("La soumission du document a échoué :", err);
      });
  };
  const handleCopy = (id) => {
    navigator.clipboard
      .writeText(id)
      .then(() => {
        toast.success("ID copié avec succès - تم نسخ المعرف بنجاح");
      })
      .catch(() => {
        toast.error("Échec de la copie de l'ID");
      });
  };

  return (
    <div className="bg-white p-5 rounded-xl">
      {isLoading && <Loader />}
      <div className="flex justify-center items-center mb-2">
        <p>ID:</p>
        <div className="flex justify-center items-center ml-3 bg-mainBg rounded-md border-2 border-[#00eafb]">
          <p className=" px-2 ">{myOrder._id}</p>
          <span
            onClick={() => handleCopy(myOrder._id)}
            className="bg-[#00eafb] p-1 w-full h-full rounded-r-md cursor-pointer text-slate-600"
          >
            {" "}
            <FaCopy className=" text-2xl " />{" "}
          </span>
        </div>
      </div>

      <div className="flex lg:block  gap-3">
        <div className="flex flex-col gap-1 p-2">
          <h2 className="text-slate-600 font-semibold">
            Livrer à : {myOrder.shippingInfo?.name}
          </h2>
          <p className="p-1">
            <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
              Adresse
            </span>
            <span className="text-slate-600 text-sm">
              {myOrder.shippingInfo?.address} {myOrder.shippingInfo?.province}{" "}
              {myOrder.shippingInfo?.city} {myOrder.shippingInfo?.area}
            </span>
          </p>
          <p className="p-1">
            <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
              Email
            </span>
            <span className="text-slate-600 text-sm">{userInfo.email}</span>
          </p>
          <p className="p-1">
            <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
              Date
            </span>
            <span className="text-slate-600 text-sm">{myOrder.date}</span>
          </p>
        </div>
        <div className="text-slate-600 p-2">
          <h2 className="p-1">
            <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded">
              Prix
            </span>{" "}
            {myOrder.price} DA inclure les frais d'expédition
          </h2>
          <p className="p-1">
            Statut de paiement:{" "}
            <span
              className={`py-[1px] text-xs px-3 ${
                myOrder.payment_status === "paid"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              } rounded-md `}
            >
              {myOrder.payment_status}
            </span>
          </p>
          <p className="p-1">
            Statut de la commande :{" "}
            <span
              className={`py-[1px] text-xs px-3 ${
                myOrder.delivery_status === "paid"
                  ? "bg-indigo-100 text-indigo-800"
                  : "bg-red-100 text-red-800"
              } rounded-md `}
            >
              {myOrder.delivery_status}
            </span>
          </p>
        </div>
      </div>
      <div className="mt-3">
        <h2 className="text-slate-600 text-lg pb-2">Produits - منتجات </h2>
        <div className="flex gap-5 flex-col">
          {myOrder.products?.map((p, i) => (
            <div key={i}>
              <div className="flex md:block gap-5 justify-start items-center text-slate-600">
                <div className="flex gap-2">
                  <img
                    className="w-[55px] h-[55px] rounded-lg"
                    src={p.images[0]}
                    alt={p.name}
                  />
                  <div className="flex text-sm flex-col justify-start items-start">
                    <Link>{p.name}</Link>
                    <p>
                      <span>Company: {p.brand} | </span>
                      <span>Quantité: {p.quantity}</span>
                    </p>
                  </div>
                </div>
                <div className="pl-4 md:mt-4">
                  {p.discount > 0 ? (
                    <div className="flex ">
                      <p className="line-through text-lg  text-orange-500">
                        {p.price}
                      </p>
                      <h2 className="text-lg text-[#00ae0f] ml-3 font-bold">
                        {p.price - Math.floor((p.price * p.discount) / 100)} DA
                      </h2>
                      <div className="mx-4 flex justify-center items-center text-white w-[30px] h-[30px] rounded-full bg-red-500 font-semibold text-xs">
                        -{p.discount}%
                      </div>
                    </div>
                  ) : (
                    <div className="flex">
                      <h2 className="text-lg text-blue-900 font-bold">
                        {p.price} DA
                      </h2>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Order;
